/**
 * Unified SCM context for reports — comparisons, partners, CAPEX variance.
 */
import type { SupplyChainPlan, Supplier } from '../types/supplyChain';
import { buildQuoteComparison, bomIdsWithComparableQuotes } from './buildQuoteComparison';
import { pickRecommendedQuoteId } from './quoteComparisonScore';
import { buildCapexVarianceTable, itemizedCapexFromBom } from './itemizedCapex';
import { isDatacratePartner } from './supplierPartnership';
import { computeCriticalPathResolved, type ProjectPhaseLike } from './criticalPathAnalysis';
import { SCM_DATA_DISCLAIMER } from './scmDisclaimer';

export interface SupplyChainReportContext {
  disclaimer: string;
  partnerSupplierCount: number;
  comparableQuoteLineCount: number;
  recommendedAwards: { bomItemId: string; bomItemName: string; quoteId: string; supplierName: string }[];
  bomCapexTotalUsd: number;
  capexVariancePct: number | null;
  criticalPathItemCount: number;
}

export function buildSupplyChainReportContext(args: {
  plan: SupplyChainPlan;
  cod: string | null;
  modelCapexUsd: number | null;
  directorySuppliers?: readonly Supplier[];
  /**
   * D1 — project phases (from `effectivePhases(intent).phases` /
   * `projectPhasesForCriticalPath(intent)`). When supplied for a genuinely
   * multi-phase, phase-tagged BoM, the critical-path count scores each line
   * against ITS OWN phase COD instead of the single project `cod`. Absent /
   * single-phase = the legacy single-COD behaviour.
   */
  phases?: ProjectPhaseLike[];
}): SupplyChainReportContext {
  const { plan, cod, modelCapexUsd } = args;
  const directory = args.directorySuppliers ?? plan.suppliers;
  const partnerSupplierCount = directory.filter(isDatacratePartner).length;
  const comparableIds = bomIdsWithComparableQuotes(plan);
  const cp = computeCriticalPathResolved(plan, cod, args.phases);
  const itemized = itemizedCapexFromBom(plan);
  const variance = buildCapexVarianceTable(plan, modelCapexUsd);

  const recommendedAwards: SupplyChainReportContext['recommendedAwards'] = [];
  for (const bomItemId of comparableIds.slice(0, 5)) {
    const comparison = buildQuoteComparison(plan, bomItemId, cod);
    if (!comparison) continue;
    const recId = pickRecommendedQuoteId(comparison.rows);
    if (!recId) continue;
    const row = comparison.rows.find(r => r.quote.id === recId);
    if (row) {
      recommendedAwards.push({
        bomItemId,
        bomItemName: comparison.bomItem.name,
        quoteId: recId,
        supplierName: row.supplier.name,
      });
    }
  }

  return {
    disclaimer: SCM_DATA_DISCLAIMER,
    partnerSupplierCount,
    comparableQuoteLineCount: comparableIds.length,
    recommendedAwards,
    bomCapexTotalUsd: itemized.total,
    capexVariancePct: variance.totalRow.deltaPct,
    criticalPathItemCount: cp.criticalPathItems.length,
  };
}

/** Top comparison bom ids for DOCX tables — critical path first. */
export function pickReportComparisonBomIds(
  plan: SupplyChainPlan,
  cod: string | null,
  limit = 3,
  phases?: ProjectPhaseLike[],
): string[] {
  const comparable = new Set(bomIdsWithComparableQuotes(plan));
  const cp = computeCriticalPathResolved(plan, cod, phases);
  const ordered: string[] = [];
  for (const item of cp.criticalPathItems) {
    if (comparable.has(item.bomItemId) && !ordered.includes(item.bomItemId)) {
      ordered.push(item.bomItemId);
    }
  }
  for (const id of comparable) {
    if (ordered.length >= limit) break;
    if (!ordered.includes(id)) ordered.push(id);
  }
  return ordered.slice(0, limit);
}
